import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import {
  DeploymentAdminDetailBase,
  DeviceAdminDetail,
  DeviceType,
} from 'src/api/Api';
import LinkedCamera from '@mui/icons-material/LinkedCamera';
import SpeakerPhone from '@mui/icons-material/SpeakerPhone';
import { formatDate } from 'src/util/date-utils';

interface DeploymentTimelineProps {
  deployments: DeploymentAdminDetailBase[];
  devices: DeviceAdminDetail[];
  selectedDeployment?: DeploymentAdminDetailBase;
  onSelectDeployment: (deployment?: DeploymentAdminDetailBase) => void;
}

const DeploymentTimeline: React.FC<DeploymentTimelineProps> = ({
  deployments,
  devices,
  selectedDeployment,
  onSelectDeployment,
}) => {
  if (!deployments.length) {
    return null;
  }

  const handleSelect = (deployment: DeploymentAdminDetailBase) => {
    if (selectedDeployment?.deployment_id === deployment.deployment_id) {
      onSelectDeployment(undefined);
    } else {
      onSelectDeployment(deployment);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" gutterBottom>
        Deployments
      </Typography>
      <Box sx={{ position: 'relative', pl: 2 }}>
        <Box
          sx={{
            position: 'absolute',
            left: '28px',
            top: 0,
            bottom: 0,
            width: '2px',
            bgcolor: 'grey.300',
          }}
        />
        {deployments.map((deployment) => {
          const device = devices.find((d) => d.device_id === deployment.device_id);
          const isSelected =
            selectedDeployment?.deployment_id === deployment.deployment_id;

          return (
            <Box
              key={deployment.deployment_id}
              sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative' }}
            >
              <IconButton
                size="small"
                sx={{
                  bgcolor: isSelected ? 'primary.main' : 'grey.400',
                  color: 'white',
                  zIndex: 1,
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.dark' : 'grey.500',
                  },
                }}
                onClick={() => handleSelect(deployment)}
              >
                {device?.type === DeviceType.CAMERA ? (
                  <LinkedCamera fontSize="small" />
                ) : (
                  <SpeakerPhone fontSize="small" />
                )}
              </IconButton>
              <Paper
                elevation={isSelected ? 4 : 1}
                sx={{
                  ml: 2,
                  p: 1.5,
                  cursor: 'pointer',
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'grey.300',
                  width: '100%',
                }}
                onClick={() => handleSelect(deployment)}
              >
                <Typography variant="body2" fontWeight="bold">
                  {deployment.deployment_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(deployment.start_date)} - {formatDate(deployment?.end_date)}
                </Typography>
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default React.memo(DeploymentTimeline);
